class TireSticker {
  constructor() {
    // Config
    this.BASE_URL = "https://shopify-app.foundstrap.com";
    this.TYPING_DELAY = 550;
    this.COLOR_PICK_DELAY = 800;
    this.STRIPE_TOGGLE_DELAY = 400;

    // State
    this.colorMode = "HEX"; // UI mode only
    this.debounceTimer = null;
    this.lastShopifyColor = "#FFFFFF"; // HEX from swatch (non-custom)
    this.activeColorHex = "#FFFFFF"; // Effective HEX (for API + order)
    this.customColorHex = null; // Exact HEX chosen by the customer (no CMYK round-trip)
    this.sectionId = "";

    // DOM cache
    this.container = null;
    this.colorInput = null;
    this.colorPicker = null;
    this.multicolorField = null;

    // Bootstrap
    this.init();
  }

  /* ===========================
   * Init & Lifecycle
   * =========================== */
  init() {
    this.cacheDom();
    if (!this.container) return;

    this.bindEvents();
    this.attachHiddenInputsWatcher(); // inject to all /cart/add forms and keep in sync
    this.attachFormValidation(); // validation input

    // Establish initial color from swatch if any
    this.initColorFromSwatch();

    // Initial previews
    this.generateSticker("front", true);
    this.generateSticker("rear", true);
  }

  cacheDom() {
    this.container = document.querySelector(".tire-sticker__container");
    this.sectionId = this.container?.dataset.sectionId || "";
    this.colorInput = document.querySelector(".tire-sticker__input--color");
    this.colorPicker = document.querySelector("#custom-color-picker");
    this.multicolorField = document.querySelector(
      ".tire-sticker__field--multicolor"
    );
  }

  /* ===========================
   * Event Wiring (single place)
   * =========================== */
  bindEvents() {
    this.bindTextInputs(); // 4 text inputs (uppercase + counters + debounce preview by side)
    this.bindStripeInputs(); // debounce both previews
    this.bindColorSwatch(); // swatch change → update state + toggle multicolor panel + previews
    this.bindColorPickerSync(); // picker/input change → update state + previews
    this.bindModeToggle(); // CMYK/HEX UI only (API + order always HEX)
  }

  /* ===========================
   * Forms (self-healing)
   * =========================== */
  attachHiddenInputsWatcher() {
    // Buat observer di luar agar bisa direferensi ulang di applyToAllForms
    const obs = new MutationObserver(() => applyToAllForms());

    const applyToAllForms = () => {
      // 🚫 Hentikan observer sementara agar perubahan DOM dari kita sendiri
      // tidak memicu event observer lagi (mencegah infinite loop)
      obs.disconnect();

      const forms = this.findCartForms();

      // Sinkronisasi semua hidden inputs (text dan warna)
      forms.forEach((f) => this.ensureHiddenInputsForForm(f));
      this.updateCustomColorHiddenAll(forms);

      // 🔁 Aktifkan kembali observer setelah selesai memodifikasi DOM
      obs.observe(document.body, { childList: true, subtree: true });
    };

    // 🔹 Jalankan sinkronisasi awal
    applyToAllForms();

    // 🔹 Aktifkan observer untuk pantau perubahan DOM (misal variant reload)
    obs.observe(document.body, { childList: true, subtree: true });
  }

  attachFormValidation() {
    const forms = this.findCartForms();

    forms.forEach((form) => {
      // Temukan tombol submit di dalam form
      const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (!submitBtn) return;

      // Hindari duplikat binding
      if (submitBtn.__tireStickerValidationAttached) return;
      submitBtn.__tireStickerValidationAttached = true;

      submitBtn.addEventListener("click", (e) => {
        const inputs = Array.from(
          document.querySelectorAll(".tire-sticker__input--text")
        );

        let hasError = false;

        inputs.forEach((input) => {
          const field = input.closest(".tire-sticker__field");
          const helper = field?.querySelector(".tire-sticker__helper-text");
          const value = (input.value || "").trim();

          if (!value) {
            hasError = true;
            input.classList.add("tire-sticker__input--error");

            if (helper) {
              const msg = helper.dataset.helperTextEmpty || "";
              helper.textContent = msg;
              helper.classList.add("tire-sticker__helper-text--error");
              helper.style.display = "block";
            }
          } else {
            input.classList.remove("tire-sticker__input--error");
            if (helper) {
              helper.textContent = "";
              helper.style.display = "none";
            }
          }
        });

        if (hasError) {
          e.preventDefault();
          e.stopImmediatePropagation(); // ⛔ hentikan semua handler Add to Cart
          const firstError = document.querySelector(".tire-sticker__helper-text--error");
          if (firstError) firstError.focus();
        }
      });
    });
    }

  findCartForms() {
    return Array.from(
      document.querySelectorAll(
        'form[action$="/cart/add"], form[action*="/cart/add"]'
      )
    );
  }

  ensureHiddenInputsForForm(form) {
    // Create/Sync 4 text properties (label-driven, multilingual-safe)
    const textInputs = Array.from(
      document.querySelectorAll(
        `.tire-sticker__input--text[id*="${this.sectionId}"]`
      )
    );

    textInputs.forEach((input) => {
      const labelEl = document.querySelector(`label[for="${input.id}"]`);
      const labelText = labelEl ? labelEl.textContent.trim() : input.id;
      const hiddenName = `properties[${labelText}]`;

      let hidden = form.querySelector(`[name="${hiddenName}"]`);
      if (!hidden) {
        hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = hiddenName;
        form.appendChild(hidden);
      }

      const sync = () => {
        hidden.value = (input.value || "").trim().toUpperCase();
      };

      input.removeEventListener("input", sync); // avoid duplicate binding if observer fires multiple times
      input.addEventListener("input", sync);
      sync(); // initial
    });
  }

  /* ===========================
   * Inputs & UI
   * =========================== */

  bindTextInputs() {
    const inputs = Array.from(
      document.querySelectorAll(".tire-sticker__input--text")
    );
    inputs.forEach((input) => {
      const field = input.closest(".tire-sticker__field");
      const counter = field?.querySelector(".tire-sticker__char-count");
      const max = parseInt(input.getAttribute("maxlength") || 13, 10);
      const position = input.id.includes("rear") ? "rear" : "front";

      const updateCount = () => {
        input.value = input.value.toUpperCase();
        const len = input.value.length;
        if (counter) {
          counter.textContent = `${len}/${max}`;
          if (len === 0) {
            input.style.removeProperty("--input-border-color");
            counter.style.color = "";
          } else {
            let colorVar = "--tire-sticker-color-success";
            if (len >= max) colorVar = "--tire-sticker-color-danger";
            else if (len >= max - 3) colorVar = "--tire-sticker-color-warning";
            const color = getComputedStyle(document.documentElement)
              .getPropertyValue(colorVar)
              .trim();
            input.style.setProperty("--input-border-color", color);
            counter.style.color = color;
          }
        }
      };

      // Initial count setup
      updateCount();

      input.addEventListener("input", () => {
        updateCount();

        const helper = field?.querySelector(".tire-sticker__helper-text");
        if (helper && input.value.trim() !== "") {
          helper.textContent = "";
          helper.classList.remove("tire-sticker__helper-text--error");
          helper.style.display = "none";
        }
        input.classList.remove("tire-sticker__input--error");

        this.debounce(() => this.generateSticker(position), this.TYPING_DELAY);
      });
    });
  }

  bindStripeInputs() {
    const stripeGroup = this.getStripeGroup();
    if (!stripeGroup) return;

    const stripeInputs = stripeGroup.querySelectorAll('input[type="radio"]');
    if (!stripeInputs.length) return;

    // Remove previous listeners to avoid duplicate bindings
    stripeInputs.forEach((input) => {
      input.removeEventListener("change", this._handleStripeChange);
    });

    // Define handler that regenerates both previews when toggled
    this._handleStripeChange = () => {
      this.debounce(() => {
        this.generateSticker("front");
        this.generateSticker("rear");
      }, this.STRIPE_TOGGLE_DELAY);
    };

    // Attach handler to both radio inputs
    stripeInputs.forEach((input) => {
      input.addEventListener("change", this._handleStripeChange);
    });
  }

  bindColorSwatch() {
    const radios = Array.from(
      document.querySelectorAll(
        '.product-form__controls-group input[type="radio"]'
      )
    ).filter((input) => {
      const label = document.querySelector(`label[for="${input.id}"]`);
      return label && label.classList.contains("color-swatch");
    });

    radios.forEach((input) => {
      input.addEventListener("change", () => {
        // Read swatch color
        const label = document.querySelector(`label[for="${input.id}"]`);
        const style = label?.getAttribute("style") || "";
        const match = style.match(/--swatch-color:\s*([^;]+)/i);
        const swatch = match ? (match[1] || "").trim() : "";

        // Toggle custom panel
        if (/url\(/i.test(swatch)) {
          this.multicolorField?.classList.add("tire-sticker__field--active");
          this.showPickerPanel(); // open the color palette by default on "Custom"
        } else {
          this.multicolorField?.classList.remove("tire-sticker__field--active");
          this.hidePickerPanel();
        }

        // Cache non-custom swatch color for API if available
        if (swatch && !/url\(/i.test(swatch)) {
          this.lastShopifyColor = this.normalizeToHex(swatch);
        }

        // Recompute effective color and update hidden custom color HEX
        this.computeEffectiveColorFromUI();
        this.updateCustomColorHiddenAll();

        // Refresh both previews
        this.debounce(() => {
          this.generateSticker("front");
          this.generateSticker("rear");
        }, this.COLOR_PICK_DELAY);
      });
    });
  }

  bindColorPickerSync() {
    if (!this.colorInput || !this.colorPicker) return;

    // Toggle panel via button (multi-language safe)
    const pickerBtn = document.querySelector(".tire-sticker__button--picker");
    const panel = document.querySelector(".tire-sticker__color-picker");

    if (pickerBtn && panel) {
      const openLabel = pickerBtn.dataset.labelOpen || "Pick Color";
      const closeLabel = pickerBtn.dataset.labelClose || "Close Picker";

      pickerBtn.addEventListener("click", () => {
        const visible = panel.classList.toggle("tire-sticker__color-picker--visible");
        pickerBtn.textContent = visible ? closeLabel : openLabel;
      });
    }

    // Picker → Input
    this.colorPicker.addEventListener("color-changed", (e) => {
      const hex = this.normalizeToHex(e.detail.value);
      this.customColorHex = hex; // keep exact HEX, even if UI displays CMYK
      this.colorInput.value =
        this.colorMode === "HEX"
          ? hex.toUpperCase()
          : this.hexToCmykString(hex);
      this.computeEffectiveColorFromUI();
      this.updateCustomColorHiddenAll();
      this.debounce(() => {
        this.generateSticker("front");
        this.generateSticker("rear");
      }, this.COLOR_PICK_DELAY);
    });

    // Input → Picker (typing and paste)
    const onColorInput = (val) => {
      if (val.startsWith("#") && val.length < 7) return; // allow partial hex typing
      const hex = this.normalizeToHex(val);
      this.customColorHex = hex;
      this.colorInput.value =
        this.colorMode === "HEX"
          ? hex.toUpperCase()
          : this.hexToCmykString(hex);
      if (this.colorPicker.color !== hex) this.colorPicker.color = hex;
      this.computeEffectiveColorFromUI();
      this.updateCustomColorHiddenAll();
      this.debounce(() => {
        this.generateSticker("front");
        this.generateSticker("rear");
      }, this.COLOR_PICK_DELAY);
    };

    this.colorInput.addEventListener("input", (e) =>
      onColorInput(e.target.value.trim())
    );
    this.colorInput.addEventListener("paste", (e) => {
      e.preventDefault();
      const t = (e.clipboardData || window.clipboardData)
        .getData("text")
        .trim();
      onColorInput(t);
    });
  }

  bindModeToggle() {
    const modeButton = document.querySelector(".tire-sticker__mode-button");
    const modeLabel = modeButton?.querySelector(".tire-sticker__mode-label");
    if (!modeButton || !modeLabel || !this.colorInput || !this.colorPicker)
      return;

    modeButton.addEventListener("click", () => {
      this.colorMode = this.colorMode === "HEX" ? "CMYK" : "HEX";
      modeLabel.textContent = this.colorMode;

      const icon = modeButton.querySelector(
        ".tire-sticker__mode-button-icon svg"
      );
      if (icon) {
        icon.classList.add("is-animating");
        setTimeout(() => icon.classList.remove("is-animating"), 300);
      }

      // Use the stored exact HEX so toggling HEX ↔ CMYK never alters the color
      const hex =
        this.customColorHex || this.normalizeToHex(this.colorInput.value);
      this.customColorHex = hex;
      this.colorInput.value =
        this.colorMode === "HEX"
          ? hex.toUpperCase()
          : this.hexToCmykString(hex);
      this.colorPicker.color = hex;
    });
  }

  showPickerPanel() {
    const panel = document.querySelector(".tire-sticker__color-picker");
    const btn = document.querySelector(".tire-sticker__button--picker");
    if (panel && btn) {
      panel.classList.add("tire-sticker__color-picker--visible");
      btn.textContent = btn.dataset.labelClose || "Close Picker";
    }
  }

  hidePickerPanel() {
    const panel = document.querySelector(".tire-sticker__color-picker");
    const btn = document.querySelector(".tire-sticker__button--picker");
    if (panel && btn) {
      panel.classList.remove("tire-sticker__color-picker--visible");
      btn.textContent = btn.dataset.labelOpen || "Pick Color";
    }
  }

  /* ===========================
   * Unified Stripe Variant Handling
   * =========================== */

  getStripeGroup() {
    // Find all variant groups in the product form
    const groups = Array.from(document.querySelectorAll(".product-form__controls-group"));
    if (!groups.length) return null;

    // Identify the group with only two radio inputs (likely Stripe)
    let stripeGroup = groups.find((g) => g.querySelectorAll('input[type="radio"]').length === 2);

    // Fallback to the last group if none matched
    if (!stripeGroup) stripeGroup = groups[groups.length - 1];

    return stripeGroup || null;
  }
  /* ===========================
   * Color Pipeline (single source)
   * =========================== */

  initColorFromSwatch() {
    // Read current checked swatch on load
    const checked = Array.from(
      document.querySelectorAll(
        '.product-form__controls-group input[type="radio"]:checked'
      )
    ).find((input) => {
      const label = document.querySelector(`label[for="${input.id}"]`);
      return label && label.classList.contains("color-swatch");
    });

    if (!checked) return;

    const label = document.querySelector(`label[for="${checked.id}"]`);
    const style = label?.getAttribute("style") || "";
    const match = style.match(/--swatch-color:\s*([^;]+)/i);
    const swatch = match ? (match[1] || "").trim() : "";

    if (/url\(/i.test(swatch)) {
      this.multicolorField?.classList.add("tire-sticker__field--active");
      this.showPickerPanel(); // "Custom" already selected on load → open palette
    } else {
      this.multicolorField?.classList.remove("tire-sticker__field--active");
      if (swatch) this.lastShopifyColor = this.normalizeToHex(swatch);
    }
    this.computeEffectiveColorFromUI();
    this.updateCustomColorHiddenAll();
  }

  isCustomVariantSelected() {
    const checked = Array.from(
      document.querySelectorAll(
        '.product-form__controls-group input[type="radio"]:checked'
      )
    ).find((input) => {
      const label = document.querySelector(`label[for="${input.id}"]`);
      return label && label.classList.contains("color-swatch");
    });
    if (!checked) return false;

    const label = document.querySelector(`label[for="${checked.id}"]`);
    const style = label?.getAttribute("style") || "";
    const match = style.match(/--swatch-color:\s*([^;]+)/i);
    const swatch = match ? (match[1] || "").trim() : "";
    return /url\(/i.test(swatch);
  }

  computeEffectiveColorFromUI() {
    // If custom panel active and input has value → use it, else use lastShopifyColor
    const useCustom = this.multicolorField?.classList.contains(
      "tire-sticker__field--active"
    );
    if (useCustom && this.colorInput?.value) {
      // Prefer the exact HEX picked/typed; fallback to parsing the input
      this.activeColorHex =
        this.customColorHex ||
        this.normalizeToHex(this.colorInput.value.trim());
    } else {
      this.activeColorHex = this.lastShopifyColor || "#FFFFFF";
    }
  }

  /* ===========================
   * Sticker Generator (API = HEX)
   * =========================== */
  async generateSticker(position, isInitial = false) {
    try {
      const upperLower = isInitial
        ? { upper: "UPPER TEXT", lower: "LOWER TEXT" }
        : this.getTextInputs(position);

      // Safety fallback if field empty
      if (!upperLower.upper) upperLower.upper = "UPPER TEXT";
      if (!upperLower.lower) upperLower.lower = "LOWER TEXT";

      const colorHex = this.activeColorHex || "#FFFFFF";
      const toggleLines = this.getStripeToggle();

      const previews = document.querySelectorAll(
        `.tire-sticker__preview-side--${position}`
      );
      if (!previews.length) return;

      const {
        rInner,
        rOuter,
        stripeRInner,
        stripeROuter,
        stripeLeftStartOuter,
        stripeLeftEndOuter,
        stripeLeftStartInner,
        stripeLeftEndInner,
        stripeRightStartOuter,
        stripeRightEndOuter,
        stripeRightStartInner,
        stripeRightEndInner,
      } = this.getTireGeometry(position);

      previews.forEach((preview) => this.showSpinnerEl(preview));

      const res = await fetch(`${this.BASE_URL}/app/generate-sticker`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text1: upperLower.upper,
          text2: upperLower.lower,
          rInner,
          rOuter,
          stripeRInner,
          stripeROuter,
          color: colorHex, // API always HEX
          toggleLines,
          stripeLeftStartOuter,
          stripeLeftEndOuter,
          stripeLeftStartInner,
          stripeLeftEndInner,
          stripeRightStartOuter,
          stripeRightEndOuter,
          stripeRightStartInner,
          stripeRightEndInner,
          compFactor: 0.35
        }),
      });

      const type = res.headers.get("Content-Type") || "";
      let content = "";

      if (type.includes("image/svg")) {
        content = await res.text();
      } else if (type.includes("image/png")) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        content = `<img src="${url}" alt="${position} sticker preview" style="width:100%;height:100%;object-fit:contain;">`;
      }

      previews.forEach((preview) => {
        const overlay = preview.querySelector(".tire-sticker__preview-overlay");
        if (overlay) {
          overlay.innerHTML = content;
          // ✨ Italique réglable depuis l'éditeur de thème (data-text-skew)
          try {
            this.applyItalic(preview, overlay, toggleLines);
          } catch (italicErr) {
            console.warn("Tire sticker italic:", italicErr);
          }
        }
        this.hideSpinnerEl(preview);
      });
    } catch (err) {
      console.error(`Error generating ${position} sticker:`, err);
    }
  }

  /* ===========================
   * ✨ Italic (text skew) — preview only
   * Value comes from the theme editor: data-text-skew="7" → 7 %
   * =========================== */
  applyItalic(preview, overlay, toggleLines) {
    const k = parseFloat(preview.dataset.textSkew || "0") / 100;
    if (!k || !isFinite(k)) return;

    const sectors = toggleLines ? this.getStripeSectors(preview) : [];

    const svg = overlay.querySelector("svg");
    if (svg) {
      this.italicizeSvg(svg, k, sectors, preview);
      return;
    }

    const img = overlay.querySelector("img");
    if (img) this.italicizeImg(img, k);
  }

  // Angular zones of the stripes (left / right) — never skewed
  getStripeSectors(preview) {
    const d = preview.dataset;
    const n = (v) => parseFloat(v);
    const mk = (s1, s2, e1, e2) => {
      const a = Math.min(n(s1), n(s2));
      const b = Math.max(n(e1), n(e2));
      return isFinite(a) && isFinite(b) ? [a - 2, b + 2] : null;
    };
    return [
      mk(d.stripeLeftStartInner, d.stripeLeftStartOuter, d.stripeLeftEndInner, d.stripeLeftEndOuter),
      mk(d.stripeRightStartInner, d.stripeRightStartOuter, d.stripeRightEndInner, d.stripeRightEndOuter),
    ].filter(Boolean);
  }

  inSectors(deg, sectors) {
    return sectors.some(([a, b]) => {
      const span = b - a;
      if (span >= 360) return true;
      const dd = (((deg - a) % 360) + 360) % 360;
      return dd <= span;
    });
  }

  isInsideDefs(el) {
    let node = el.parentNode;
    while (node && node.nodeType === 1) {
      const tag = (node.tagName || "").toLowerCase();
      if (tag === "svg") return false;
      if (["defs", "clippath", "mask", "pattern", "symbol", "marker"].includes(tag)) return true;
      node = node.parentNode;
    }
    return false;
  }

  // Wait until a hidden preview (desktop/mobile duplicate) becomes visible
  whenVisible(preview, cb) {
    if (preview.__italicWait) {
      preview.__italicWait.cb = cb;
      return;
    }
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (preview.offsetWidth > 0 && preview.offsetHeight > 0) {
        ro.disconnect();
        const fn = preview.__italicWait && preview.__italicWait.cb;
        preview.__italicWait = null;
        if (fn) fn();
      }
    });
    preview.__italicWait = { cb, ro };
    ro.observe(preview);
  }

  italicizeSvg(svg, k, sectors, preview) {
    if (!svg.isConnected || svg.dataset.italicDone === "1") return;

    this.inlineSvgUses(svg);

    const texts = Array.from(svg.querySelectorAll("text")).filter(
      (t) => !this.isInsideDefs(t)
    );

    if (texts.length) {
      // Case 1: real SVG text (often on a <textPath>) → one letter at a time
      const measurable = texts.every((t) => {
        if (!t.textContent.trim()) return true;
        try {
          return t.getComputedTextLength() > 0;
        } catch (e) {
          return false;
        }
      });
      if (!measurable) {
        this.whenVisible(preview, () => this.italicizeSvg(svg, k, sectors, preview));
        return;
      }
      texts.forEach((t) => this.splitTextWithSkew(t, k));
    } else {
      // Case 2: letters already converted to outlines (<path>)
      const paths = Array.from(svg.querySelectorAll("path")).filter(
        (p) => !this.isInsideDefs(p)
      );
      if (paths.length) this.shearSvgPaths(svg, paths, k, sectors);
    }

    svg.dataset.italicDone = "1";
  }

  inlineSvgUses(svg) {
    const NS = "http://www.w3.org/2000/svg";
    svg.querySelectorAll("use").forEach((u) => {
      const href =
        u.getAttribute("href") ||
        u.getAttributeNS("http://www.w3.org/1999/xlink", "href");
      if (!href || href.charAt(0) !== "#") return;
      const ref = svg.querySelector(`[id="${href.slice(1)}"]`);
      if (!ref) return;

      const g = document.createElementNS(NS, "g");
      const x = parseFloat(u.getAttribute("x")) || 0;
      const y = parseFloat(u.getAttribute("y")) || 0;
      const tr = `${u.getAttribute("transform") || ""} translate(${x} ${y})`.trim();
      g.setAttribute("transform", tr);
      ["fill", "stroke", "stroke-width", "class", "style", "opacity"].forEach((a) => {
        if (u.hasAttribute(a)) g.setAttribute(a, u.getAttribute(a));
      });

      const clone = ref.cloneNode(true);
      clone.removeAttribute("id");
      if ((clone.tagName || "").toLowerCase() === "symbol") {
        while (clone.firstChild) g.appendChild(clone.firstChild);
      } else {
        g.appendChild(clone);
      }
      u.parentNode.replaceChild(g, u);
    });
  }

  // Case 1 — <text>: each letter becomes its own <text>, rotated and skewed
  splitTextWithSkew(textEl, k) {
    const NS = "http://www.w3.org/2000/svg";
    const n = textEl.getNumberOfChars();
    if (!n) return;

    // Same whitespace handling as SVG (xml:space="default")
    const str = textEl.textContent
      .replace(/[\r\n]/g, "")
      .replace(/\t/g, " ")
      .trim()
      .replace(/ {2,}/g, " ");
    if (str.length !== n) return; // safety: leave untouched if indexes don't match

    const angle = (-Math.atan(k) * 180) / Math.PI;
    const cs = getComputedStyle(textEl);
    const inner = textEl.querySelector("textPath, tspan") || textEl;
    const ics = getComputedStyle(inner);

    const styleProps = [
      "fill", "fill-opacity", "stroke", "stroke-width", "stroke-linejoin",
      "paint-order", "opacity", "font-family", "font-size", "font-weight",
      "font-style", "font-stretch", "letter-spacing",
    ];
    let style = styleProps
      .map((p) => {
        const v = cs.getPropertyValue(p);
        return v ? `${p}:${v}` : "";
      })
      .filter(Boolean)
      .join(";");
    const db = ics.getPropertyValue("dominant-baseline") || cs.getPropertyValue("dominant-baseline");
    const ab = ics.getPropertyValue("alignment-baseline");
    if (db) style += `;dominant-baseline:${db}`;
    if (ab) style += `;alignment-baseline:${ab}`;

    const chars = [];
    for (let i = 0; i < n; i++) {
      const ch = str.charAt(i);
      if (/\s/.test(ch)) continue;
      let pos, rot, adv;
      try {
        pos = textEl.getStartPositionOfChar(i);
        rot = textEl.getRotationOfChar(i);
        adv = textEl.getSubStringLength(i, 1);
      } catch (e) {
        return; // leave the original text untouched
      }
      chars.push({ ch, pos, rot, adv });
    }
    if (!chars.length) return;

    const g = document.createElementNS(NS, "g");
    const tr = textEl.getAttribute("transform");
    if (tr) g.setAttribute("transform", tr);
    g.setAttribute("style", style);

    const nodes = chars.map((c) => {
      const t = document.createElementNS(NS, "text");
      t.setAttribute("x", "0");
      t.setAttribute("y", "0");
      t.setAttribute("text-anchor", "start");
      const base = `translate(${c.pos.x} ${c.pos.y}) rotate(${c.rot}) skewX(${angle})`;
      t.setAttribute("transform", base);
      t.textContent = c.ch;
      g.appendChild(t);
      return { t, base, adv: c.adv };
    });

    textEl.parentNode.insertBefore(g, textEl);

    // Keep the horizontal compression the API may apply (textLength / compFactor)
    nodes.forEach(({ t, base, adv }) => {
      try {
        const natural = t.getComputedTextLength();
        if (natural > 0 && adv > 0) {
          const s = adv / natural;
          if (s < 0.98 && s > 0.3) t.setAttribute("transform", `${base} scale(${s} 1)`);
        }
      } catch (e) {}
    });

    textEl.parentNode.removeChild(textEl);
  }

  // Case 2 — outlines: polar shear around the tire center (follows the arc)
  shearSvgPaths(svg, paths, k, sectors) {
    const vb = svg.viewBox && svg.viewBox.baseVal;
    let cx, cy;
    if (vb && vb.width && vb.height) {
      cx = vb.x + vb.width / 2;
      cy = vb.y + vb.height / 2;
    } else {
      const w = parseFloat(svg.getAttribute("width")) || 0;
      const h = parseFloat(svg.getAttribute("height")) || 0;
      if (!w || !h) return;
      cx = w / 2;
      cy = h / 2;
    }

    const apply = (m, x, y) => [m.a * x + m.c * y + m.e, m.b * x + m.d * y + m.f];
    const isStripe = (X, Y) => {
      if (!sectors.length) return false;
      const deg = ((Math.atan2(Y - cy, X - cx) * 180) / Math.PI + 360) % 360;
      return this.inSectors(deg, sectors);
    };

    const items = [];
    paths.forEach((p) => {
      const d = p.getAttribute("d");
      if (!d) return;
      const segs = this.parsePath(d);
      if (!segs.length) return;
      const m = this.localToSvgMatrix(p, svg);
      items.push({ p, segs, m, inv: m.inverse() });
    });
    if (!items.length) return;

    // Split into sub-paths (each starts with M). A whole sub-path is either
    // a stripe or a letter — never half of each (avoids torn letters/stripes).
    // A stripe covers a wide angle (~80°), a letter only a few degrees.
    const subpaths = [];
    items.forEach((it) => {
      let cur = null;
      it.segs.forEach((s) => {
        if (s.c === "M" || !cur) {
          cur = { it, segs: [] };
          subpaths.push(cur);
        }
        cur.segs.push(s);
      });
    });

    const WIDE = (25 * Math.PI) / 180;
    subpaths.forEach((sp) => {
      let prev = null, acc = 0, min = 0, max = 0, sumY = 0, cnt = 0;
      sp.segs.forEach((s) =>
        s.p.forEach(([x, y]) => {
          const [X, Y] = apply(sp.it.m, x, y);
          const a = Math.atan2(Y - cy, X - cx);
          if (prev !== null) {
            let d = a - prev;
            while (d > Math.PI) d -= 2 * Math.PI;
            while (d < -Math.PI) d += 2 * Math.PI;
            acc += d;
            if (acc < min) min = acc;
            if (acc > max) max = acc;
          }
          prev = a;
          sumY += Y;
          cnt++;
        })
      );
      sp.isStripe = max - min > WIDE;
      // Top or bottom text, decided once for the whole letter
      sp.isTop = cnt ? sumY / cnt < cy : true;
    });

    // Pass 1: middle radius of the top text and of the bottom text
    const top = [Infinity, -Infinity];
    const bottom = [Infinity, -Infinity];
    subpaths.forEach((sp) => {
      if (sp.isStripe) return;
      const b = sp.isTop ? top : bottom;
      sp.segs.forEach((s) =>
        s.p.forEach(([x, y]) => {
          const [X, Y] = apply(sp.it.m, x, y);
          const r = Math.hypot(X - cx, Y - cy);
          if (r < b[0]) b[0] = r;
          if (r > b[1]) b[1] = r;
        })
      );
    });
    const rcTop = (top[0] + top[1]) / 2;
    const rcBottom = (bottom[0] + bottom[1]) / 2;

    // Pass 2: shift each point of the letters along the arc,
    // proportionally to its height in the letter (stripes untouched)
    subpaths.forEach((sp) => {
      if (sp.isStripe) return;
      const rc = sp.isTop ? rcTop : rcBottom;
      if (!isFinite(rc)) return;
      sp.segs.forEach((s) => {
        s.p = s.p.map(([x, y]) => {
          const [X, Y] = apply(sp.it.m, x, y);
          const r = Math.hypot(X - cx, Y - cy);
          if (r < 1e-6) return [x, y];
          const th = Math.atan2(Y - cy, X - cx) + (k * (r - rc)) / r;
          return apply(sp.it.inv, cx + r * Math.cos(th), cy + r * Math.sin(th));
        });
      });
    });
    items.forEach((it) => it.p.setAttribute("d", this.serializePath(it.segs)));
  }

  localToSvgMatrix(el, svg) {
    let m = new DOMMatrix();
    let node = el;
    while (node && node !== svg) {
      const tl = node.transform && node.transform.baseVal;
      if (tl && tl.numberOfItems) {
        const c = tl.consolidate();
        if (c) {
          const mm = c.matrix;
          m = new DOMMatrix([mm.a, mm.b, mm.c, mm.d, mm.e, mm.f]).multiply(m);
        }
      }
      node = node.parentNode;
    }
    return m;
  }

  // SVG path → absolute segments
  parsePath(d) {
    const tk = d.match(/[a-zA-Z]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g) || [];
    const out = [];
    let i = 0, cmd = null, x = 0, y = 0, sx = 0, sy = 0;
    const isCmd = (t) => /^[a-zA-Z]$/.test(t);
    const num = () => {
      const v = parseFloat(tk[i++]);
      if (!isFinite(v)) throw new Error("bad path");
      return v;
    };
    const pt = (rel) => [num() + (rel ? x : 0), num() + (rel ? y : 0)];

    try {
      while (i < tk.length) {
        if (isCmd(tk[i])) cmd = tk[i++];
        else if (!cmd || cmd === "Z" || cmd === "z") return [];
        const rel = cmd !== cmd.toUpperCase();
        switch (cmd.toUpperCase()) {
          case "M": {
            const p = pt(rel);
            out.push({ c: "M", p: [p] });
            [x, y] = p; sx = x; sy = y;
            cmd = rel ? "l" : "L";
            break;
          }
          case "L":
          case "T": {
            const p = pt(rel);
            out.push({ c: cmd.toUpperCase(), p: [p] });
            [x, y] = p;
            break;
          }
          case "H": {
            const v = num() + (rel ? x : 0);
            out.push({ c: "L", p: [[v, y]] });
            x = v;
            break;
          }
          case "V": {
            const v = num() + (rel ? y : 0);
            out.push({ c: "L", p: [[x, v]] });
            y = v;
            break;
          }
          case "C": {
            const p1 = pt(rel), p2 = pt(rel), p3 = pt(rel);
            out.push({ c: "C", p: [p1, p2, p3] });
            [x, y] = p3;
            break;
          }
          case "S":
          case "Q": {
            const p1 = pt(rel), p2 = pt(rel);
            out.push({ c: cmd.toUpperCase(), p: [p1, p2] });
            [x, y] = p2;
            break;
          }
          case "A": {
            const a = [num(), num(), num(), num(), num()];
            const p = pt(rel);
            out.push({ c: "A", a, p: [p] });
            [x, y] = p;
            break;
          }
          case "Z": {
            out.push({ c: "Z", p: [] });
            x = sx; y = sy;
            break;
          }
          default:
            return [];
        }
      }
    } catch (e) {
      return [];
    }
    return out;
  }

  serializePath(segs) {
    const f = (v) => +v.toFixed(3);
    return segs
      .map((s) => {
        if (s.c === "Z") return "Z";
        if (s.c === "A") {
          const [px, py] = s.p[0];
          return `A${f(s.a[0])} ${f(s.a[1])} ${s.a[2]} ${s.a[3]} ${s.a[4]} ${f(px)} ${f(py)}`;
        }
        return s.c + s.p.map(([px, py]) => `${f(px)} ${f(py)}`).join(" ");
      })
      .join("");
  }

  // Case 3 — PNG image: each letter (connected shape) is skewed along the arc.
  // Stripes and other wide shapes are detected and left untouched.
  italicizeImg(img, k) {
    if (img.dataset.italicDone === "1") return;
    img.dataset.italicDone = "1";

    const run = () => {
      const w = img.naturalWidth, h = img.naturalHeight;
      if (!w || !h) return;
      const cv = document.createElement("canvas");
      cv.width = w;
      cv.height = h;
      const ctx = cv.getContext("2d");
      ctx.drawImage(img, 0, 0);
      let src;
      try {
        src = ctx.getImageData(0, 0, w, h);
      } catch (e) {
        return;
      }
      const S = src.data;
      const N = w * h;
      const cx = w / 2, cy = h / 2;

      // 1) Connected shapes (8-neighbour) of non-transparent pixels
      const label = new Int32Array(N).fill(-1);
      const stack = new Int32Array(N);
      const comps = [];
      for (let start = 0; start < N; start++) {
        if (label[start] !== -1 || S[start * 4 + 3] === 0) continue;
        const id = comps.length;
        const bins = new Uint8Array(360);
        let sp = 0, count = 0, sumY = 0;
        stack[sp++] = start;
        label[start] = id;
        while (sp) {
          const q = stack[--sp];
          const x = q % w, y = (q - x) / w;
          count++;
          sumY += y;
          const deg = Math.floor(((Math.atan2(y + 0.5 - cy, x + 0.5 - cx) * 180) / Math.PI + 360) % 360);
          bins[deg] = 1;
          for (let dy = -1; dy <= 1; dy++) {
            const yy = y + dy;
            if (yy < 0 || yy >= h) continue;
            for (let dx = -1; dx <= 1; dx++) {
              const xx = x + dx;
              if (xx < 0 || xx >= w) continue;
              const n = yy * w + xx;
              if (label[n] === -1 && S[n * 4 + 3] !== 0) {
                label[n] = id;
                stack[sp++] = n;
              }
            }
          }
        }
        // Angular span = 360° minus the largest empty gap
        let gap = 0, run0 = 0, best = 0;
        for (let i = 0; i < 720; i++) {
          if (bins[i % 360]) run0 = 0;
          else if (++run0 > best) best = run0;
        }
        gap = Math.min(best, 360);
        const span = 360 - gap;
        comps.push({ letter: span <= 45, top: sumY / count < cy });
      }

      // 2) Middle radius of the top letters and of the bottom letters
      const top = [Infinity, -Infinity];
      const bottom = [Infinity, -Infinity];
      for (let q = 0; q < N; q++) {
        const id = label[q];
        if (id < 0 || !comps[id].letter) continue;
        const x = q % w, y = (q - x) / w;
        const r = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
        const b = comps[id].top ? top : bottom;
        if (r < b[0]) b[0] = r;
        if (r > b[1]) b[1] = r;
      }
      if (!isFinite(top[0]) && !isFinite(bottom[0])) return;
      const rcTop = (top[0] + top[1]) / 2;
      const rcBottom = (bottom[0] + bottom[1]) / 2;

      // 3) Base = everything except the letters; letters layer = letters only
      const out = ctx.createImageData(w, h);
      const D = out.data;
      const L = new Uint8ClampedArray(N * 4);
      const isTopL = new Uint8Array(N);
      for (let q = 0; q < N; q++) {
        const o = q * 4, id = label[q];
        if (id >= 0 && comps[id].letter) {
          L[o] = S[o]; L[o + 1] = S[o + 1]; L[o + 2] = S[o + 2]; L[o + 3] = S[o + 3];
          isTopL[q] = comps[id].top ? 1 : 2;
        } else {
          D[o] = S[o]; D[o + 1] = S[o + 1]; D[o + 2] = S[o + 2]; D[o + 3] = S[o + 3];
        }
      }

      // Bilinear sample of the letters layer (premultiplied), restricted to one half
      const tmp = [0, 0, 0, 0];
      const sample = (sx, sy, half) => {
        const x0 = Math.floor(sx), y0 = Math.floor(sy);
        const fx = sx - x0, fy = sy - y0;
        tmp[0] = tmp[1] = tmp[2] = tmp[3] = 0;
        for (let j = 0; j < 2; j++) {
          for (let i = 0; i < 2; i++) {
            const xx = x0 + i, yy = y0 + j;
            if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
            const q = yy * w + xx;
            if (isTopL[q] !== half) continue;
            const wt = (i ? fx : 1 - fx) * (j ? fy : 1 - fy);
            const a = L[q * 4 + 3] * wt;
            tmp[0] += L[q * 4] * a;
            tmp[1] += L[q * 4 + 1] * a;
            tmp[2] += L[q * 4 + 2] * a;
            tmp[3] += a;
          }
        }
        return tmp;
      };

      // 4) Warp the letters along the arc and paint them over the base
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const dx = x + 0.5 - cx, dy = y + 0.5 - cy;
          const r = Math.hypot(dx, dy);
          if (r < 1) continue;
          const th = Math.atan2(dy, dx);
          for (let half = 1; half <= 2; half++) {
            const rc = half === 1 ? rcTop : rcBottom;
            const band = half === 1 ? top : bottom;
            if (!isFinite(rc) || r < band[0] - 3 || r > band[1] + 3) continue;
            const t0 = th - (k * (r - rc)) / r;
            const c = sample(cx + r * Math.cos(t0) - 0.5, cy + r * Math.sin(t0) - 0.5, half);
            const a = c[3];
            if (a <= 0) continue;
            const o = (y * w + x) * 4;
            const sa = a / 255, da = D[o + 3] / 255;
            const oa = sa + da * (1 - sa);
            if (oa <= 0) continue;
            for (let ch = 0; ch < 3; ch++) {
              const sc = c[ch] / a;
              D[o + ch] = (sc * sa + D[o + ch] * da * (1 - sa)) / oa;
            }
            D[o + 3] = oa * 255;
          }
        }
      }
      ctx.putImageData(out, 0, 0);
      img.src = cv.toDataURL("image/png");
    };

    if (img.complete && img.naturalWidth) run();
    else img.addEventListener("load", run, { once: true });
  }

  /* ===========================
   * Hidden inputs: Custom Color HEX (sent to order / admin)
   * =========================== */
  updateCustomColorHiddenAll(forms = null) {
    const allForms = forms || this.findCartForms();
    const isCustom = this.isCustomVariantSelected();
    const hex = (this.activeColorHex || "#FFFFFF").toUpperCase(); // e.g. #1A2B3C
    const name = "properties[Custom Color]";

    allForms.forEach((form) => {
      const existing = form.querySelector(`[name="${name}"]`);

      if (isCustom) {
        if (!existing) {
          const hidden = document.createElement("input");
          hidden.type = "hidden";
          hidden.name = name;
          hidden.value = hex;
          form.appendChild(hidden);
        } else {
          existing.value = hex;
        }
      } else {
        if (existing) existing.remove();
      }
    });
  }


  /* ===========================
   * Helpers
   * =========================== */
  debounce(fn, delay) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(fn, delay);
  }

  getTextInputs(position) {
    const sectionId = this.sectionId || "";
    const uppers = document.querySelectorAll(
      `#tire-sticker-${position}-top-${sectionId}`
    );
    const lowers = document.querySelectorAll(
      `#tire-sticker-${position}-bottom-${sectionId}`
    );

    const getVal = (nodes) => {
      for (const node of nodes) {
        const val = node?.value.trim();
        if (val) return val.toUpperCase();
      }
      return "";
    };

    return {
      upper: getVal(uppers),
      lower: getVal(lowers),
    };
  }

  getTireGeometry(position) {
    const el = document.querySelector(
      `.tire-sticker__preview-side--${position}`
    );
    if (!el) {
      return {
        rInner: 0,
        rOuter: 0,
        stripeRInner: 0,
        stripeROuter: 0,
        stripeLeftStartOuter: 0,
        stripeLeftEndOuter: 0,
        stripeLeftStartInner: 0,
        stripeLeftEndInner: 0,
        stripeRightStartOuter: 0,
        stripeRightEndOuter: 0,
        stripeRightStartInner: 0,
        stripeRightEndInner: 0,
      };
    }
    return {
      rInner: parseFloat(el.dataset.tireRInner || 0),
      rOuter: parseFloat(el.dataset.tireROuter || 0),
      stripeRInner: parseFloat(el.dataset.stripeRInner || 0),
      stripeROuter: parseFloat(el.dataset.stripeROuter || 0),
      stripeLeftStartOuter: parseFloat(el.dataset.stripeLeftStartOuter || 0),
      stripeLeftEndOuter: parseFloat(el.dataset.stripeLeftEndOuter || 0),
      stripeLeftStartInner: parseFloat(el.dataset.stripeLeftStartInner || 0),
      stripeLeftEndInner: parseFloat(el.dataset.stripeLeftEndInner || 0),
      stripeRightStartOuter: parseFloat( el.dataset.stripeRightStartOuter || 0),
      stripeRightEndOuter: parseFloat(el.dataset.stripeRightEndOuter || 0),
      stripeRightStartInner: parseFloat(el.dataset.stripeRightStartInner || 0),
      stripeRightEndInner: parseFloat(el.dataset.stripeRightEndInner || 0),
    };
  }

  getStripeToggle() {
    const stripeGroup = this.getStripeGroup();
    if (!stripeGroup) return true;

    const checked = stripeGroup.querySelector('input[type="radio"]:checked');
    if (!checked) return true;

    const value = (checked.value || "").trim().toLowerCase();
    const yesWords = ["yes", "oui"];
    const noWords = ["no", "non"];

    // Determine toggle state by known words or fallback to index
    if (yesWords.includes(value)) return true;
    if (noWords.includes(value)) return false;

    const radios = Array.from(stripeGroup.querySelectorAll('input[type="radio"]'));
    return radios.indexOf(checked) === 0;
  }

  showSpinnerEl(preview) {
    const spinner = preview.querySelector(".tire-sticker__spinner");
    if (spinner) spinner.classList.add("is-visible");
  }

  hideSpinnerEl(preview) {
    const spinner = preview.querySelector(".tire-sticker__spinner");
    if (spinner) spinner.classList.remove("is-visible");
  }

  normalizeToHex(color) {
    if (!color) return "#FFFFFF";
    const val = color.trim();

    // Already HEX
    if (/^#([0-9A-F]{3}){1,2}$/i.test(val)) return val.toUpperCase();

    // CMYK → HEX
    const cmyk = val.match(/cmyk\((\d+),\s*(\d+),\s*(\d+),\s*(\d+)\)/i);
    if (cmyk) {
      const [, c, m, y, k] = cmyk.map(Number);
      return this.cmykToHex(c, m, y, k).toUpperCase();
    }

    const rgbMatch = val.match(
      /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+[\d.]*)?\s*\)/i
    );
    if (rgbMatch) {
      const [_, r, g, b] = rgbMatch.map(Number);
      const toHex = (n) =>
        Math.max(0, Math.min(255, Math.round(n)))
          .toString(16)
          .padStart(2, "0");
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    }

    return "#FFFFFF"; // fallback safety
  }

  hexToCmykString(hex) {
    const { c, m, y, k } = this.hexToCmyk(hex);
    return `cmyk(${c}, ${m}, ${y}, ${k})`;
    // If the client wants the "C:xx% M:xx% ..." style, adapt here.
  }

  hexToCmyk(hex) {
    const r = parseInt(hex.substr(1, 2), 16) / 255;
    const g = parseInt(hex.substr(3, 2), 16) / 255;
    const b = parseInt(hex.substr(5, 2), 16) / 255;
    const k = 1 - Math.max(r, g, b);
    const c = (1 - r - k) / (1 - k) || 0;
    const m = (1 - g - k) / (1 - k) || 0;
    const y = (1 - b - k) / (1 - k) || 0;
    return {
      c: Math.round(c * 100),
      m: Math.round(m * 100),
      y: Math.round(y * 100),
      k: Math.round(k * 100),
    };
  }

  cmykToHex(c, m, y, k) {
    c /= 100;
    m /= 100;
    y /= 100;
    k /= 100;
    const r = 255 * (1 - c) * (1 - k);
    const g = 255 * (1 - m) * (1 - k);
    const b = 255 * (1 - y) * (1 - k);
    return (
      "#" +
      [r, g, b].map((x) => Math.round(x).toString(16).padStart(2, "0")).join("")
    );
  }

  /* ===========================
   * Spinner Controls
   * =========================== */
  showSpinner(position) {
    const spinner = document.querySelector(
      `.tire-sticker__preview-side--${position} .tire-sticker__spinner`
    );
    if (spinner) spinner.classList.add("is-visible");
  }

  hideSpinner(position) {
    const spinner = document.querySelector(
      `.tire-sticker__preview-side--${position} .tire-sticker__spinner`
    );
    if (spinner) spinner.classList.remove("is-visible");
  }
}

/* ===========================
 * Shopify Section Lifecycle
 * =========================== */
const initTireSticker = (evt) => {
  const container = document.querySelector(".tire-sticker__container");
  if (!container || container.dataset.initialized === "true") return;
  container.dataset.initialized = "true";
  new TireSticker();
};

["DOMContentLoaded", "shopify:section:load", "shopify:block:select"].forEach(
  (evt) => document.addEventListener(evt, initTireSticker)
);

console.log("tire sticker js loaded");
